import { useEffect, useRef, useState, FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { useSpecialtyConfig } from '@/lib/hooks/useSpecialtyConfig'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem('velink_token')}`, 'Content-Type': 'application/json' }
}

function ProfileEditSection() {
  const [profile, setProfile] = useState<{ firstName: string; lastName: string; phone: string; bio: string; siret: string; isSociete: boolean } | null>(null)
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { headers: authH() }).then((r) => r.json()).then(setProfile)
  }, [])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true); setMsg('')
    const fd = new FormData(e.currentTarget)
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH', headers: authH(),
        body: JSON.stringify({
          firstName: fd.get('firstName'),
          lastName: fd.get('lastName'),
          phone: fd.get('phone'),
          bio: fd.get('bio'),
          siret: fd.get('siret'),
          isSociete: fd.get('isSociete') === 'on',
        }),
      })
      if (!res.ok) throw new Error('Erreur')
      setMsg('Profil mis à jour ✓')
    } catch { setMsg('Erreur lors de la mise à jour') }
    finally { setLoading(false) }
  }

  if (!profile) return null
  return (
    <div className="glass-card p-6">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Modifier mon profil</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="glass-label">Prénom</label><input name="firstName" defaultValue={profile.firstName} className="glass-input" required /></div>
          <div><label className="glass-label">Nom</label><input name="lastName" defaultValue={profile.lastName} className="glass-input" required /></div>
        </div>
        <div><label className="glass-label">Téléphone</label><input name="phone" defaultValue={profile.phone ?? ''} className="glass-input" /></div>
        <div>
          <label className="glass-label">Numéro SIRET <span className="text-[#F59E0B] text-[10px]">requis</span></label>
          <input name="siret" defaultValue={profile.siret ?? ''} className="glass-input" placeholder="Ex : 12345678900010" maxLength={14} />
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" name="isSociete" id="isSociete" defaultChecked={profile.isSociete} className="w-4 h-4 accent-accent" />
          <label htmlFor="isSociete" className="text-sm text-text-secondary cursor-pointer">
            Je suis une société (Kbis requis)
          </label>
        </div>
        <div><label className="glass-label">Bio</label><textarea name="bio" defaultValue={profile.bio ?? ''} className="glass-input min-h-[80px]" /></div>
        {msg && <p className={msg.includes('✓') ? 'text-success text-sm' : 'text-danger text-sm'}>{msg}</p>}
        <button type="submit" disabled={loading} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">{loading ? 'Enregistrement…' : 'Enregistrer'}</button>
      </form>
    </div>
  )
}

function EmailSection({ currentEmail, onEmailChanged }: { currentEmail: string | null; onEmailChanged: (email: string, token: string) => void }) {
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true); setMsg('')
    const fd = new FormData(e.currentTarget)
    try {
      const res = await fetch(`${API}/api/auth/email`, {
        method: 'PATCH', headers: authH(),
        body: JSON.stringify({ newEmail: fd.get('newEmail'), currentPassword: fd.get('currentPassword') }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      onEmailChanged(fd.get('newEmail') as string, data.accessToken)
      setMsg('Adresse email mise à jour ✓')
      ;(e.target as HTMLFormElement).reset()
    } catch (err: unknown) { setMsg(err instanceof Error ? err.message : 'Erreur') }
    finally { setLoading(false) }
  }

  return (
    <div className="glass-card p-6">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Adresse email</h2>
      {currentEmail && <p className="text-text-muted text-xs mb-5">Actuelle : <span className="text-text-secondary">{currentEmail}</span></p>}
      <form onSubmit={onSubmit} className="space-y-4">
        <div><label className="glass-label">Nouvelle adresse email</label><input type="email" name="newEmail" className="glass-input" required placeholder="nouvelle@email.com" /></div>
        <div><label className="glass-label">Mot de passe actuel (confirmation)</label><input type="password" name="currentPassword" className="glass-input" required /></div>
        {msg && <p className={msg.includes('✓') ? 'text-success text-sm' : 'text-danger text-sm'}>{msg}</p>}
        <button type="submit" disabled={loading} className="glass-btn px-6 py-2.5 text-sm rounded-xl">{loading ? 'Mise à jour…' : 'Changer l\'adresse email'}</button>
      </form>
    </div>
  )
}

function PasswordSection() {
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true); setMsg('')
    const fd = new FormData(e.currentTarget)
    const newPwd = fd.get('newPassword') as string
    if (newPwd !== fd.get('confirmPassword')) { setMsg('Les mots de passe ne correspondent pas'); setLoading(false); return }
    try {
      const res = await fetch(`${API}/api/auth/password`, {
        method: 'PATCH', headers: authH(),
        body: JSON.stringify({ currentPassword: fd.get('currentPassword'), newPassword: newPwd }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      setMsg('Mot de passe mis à jour ✓')
      ;(e.target as HTMLFormElement).reset()
    } catch (err: unknown) { setMsg(err instanceof Error ? err.message : 'Erreur') }
    finally { setLoading(false) }
  }

  return (
    <div className="glass-card p-6">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Changer le mot de passe</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <div><label className="glass-label">Mot de passe actuel</label><input type="password" name="currentPassword" className="glass-input" required /></div>
        <div><label className="glass-label">Nouveau mot de passe</label><input type="password" name="newPassword" className="glass-input" minLength={8} required /></div>
        <div><label className="glass-label">Confirmer</label><input type="password" name="confirmPassword" className="glass-input" required /></div>
        {msg && <p className={msg.includes('✓') ? 'text-success text-sm' : 'text-danger text-sm'}>{msg}</p>}
        <button type="submit" disabled={loading} className="glass-btn px-6 py-2.5 text-sm rounded-xl">{loading ? 'Mise à jour…' : 'Changer le mot de passe'}</button>
      </form>
    </div>
  )
}

function LocationSection({ latitude, longitude, onUpdated }: { latitude: number | null; longitude: number | null; onUpdated: (lat: number, lng: number) => void }) {
  const [city, setCity] = useState('')
  const [savedCity, setSavedCity] = useState<string | null>(null)
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [geoLoading, setGeoLoading] = useState(false)

  // Géocodage inverse au chargement si position enregistrée
  useEffect(() => {
    if (!latitude || !longitude || savedCity !== null) return
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=fr`)
      .then((r) => r.json())
      .then((d) => setSavedCity(d.address?.city ?? d.address?.town ?? d.address?.village ?? d.address?.county ?? null))
      .catch(() => setSavedCity(''))
  }, [latitude, longitude, savedCity])

  async function saveLocation(lat: number, lng: number, cityName?: string) {
    await apiFetch('/providers/me/location', {
      method: 'PATCH',
      body: JSON.stringify({ latitude: lat, longitude: lng }),
    })
    onUpdated(lat, lng)
    if (cityName) setSavedCity(cityName)
  }

  async function handleCitySearch() {
    if (!city.trim()) return
    setLoading(true); setMsg('')
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)}&format=json&limit=1&countrycodes=fr`, {
        headers: { 'Accept-Language': 'fr' },
      })
      const data = await res.json()
      if (!data.length) { setMsg('Ville non trouvée'); return }
      const lat = parseFloat(data[0].lat)
      const lng = parseFloat(data[0].lon)
      const cityName = data[0].display_name.split(',')[0]
      await saveLocation(lat, lng, cityName)
      setMsg(`✅ Position enregistrée : ${cityName}`)
      setCity('')
    } catch { setMsg('Erreur lors de la recherche') }
    finally { setLoading(false) }
  }

  function handleAutoLocate() {
    if (!navigator.geolocation) { setMsg('Géolocalisation non supportée par votre navigateur'); return }
    setGeoLoading(true); setMsg('')
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          // Géocodage inverse pour obtenir la ville
          const rev = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&accept-language=fr`)
          const revData = await rev.json()
          const cityName = revData.address?.city ?? revData.address?.town ?? revData.address?.village ?? null
          await saveLocation(pos.coords.latitude, pos.coords.longitude, cityName ?? undefined)
          setMsg(`✅ Position enregistrée${cityName ? ` : ${cityName}` : ''}`)
        } catch { setMsg('Erreur lors de la mise à jour') }
        finally { setGeoLoading(false) }
      },
      () => { setMsg('Accès à la position refusé'); setGeoLoading(false) },
    )
  }

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Ma position</h2>
        {latitude && longitude && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-green-500/15 border border-green-500/30 text-green-400">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            {savedCity ? `Enregistrée · ${savedCity}` : 'Enregistrée'}
          </span>
        )}
      </div>
      <p className="text-text-secondary text-sm mb-5 leading-relaxed">
        Votre position permet de recevoir des missions à proximité de chez vous.
      </p>

      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCitySearch()}
            placeholder="Entrez votre ville ou adresse…"
            className="glass-input flex-1"
          />
          <button
            onClick={handleCitySearch}
            disabled={loading || !city.trim()}
            className="glass-btn px-4 py-2.5 text-sm rounded-xl whitespace-nowrap"
          >
            {loading ? '…' : 'Rechercher'}
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-white/[0.08]" />
          <span className="text-text-muted text-xs">ou</span>
          <div className="flex-1 h-px bg-white/[0.08]" />
        </div>

        <button
          onClick={handleAutoLocate}
          disabled={geoLoading}
          className="glass-btn w-full py-2.5 text-sm rounded-xl flex items-center justify-center gap-2"
        >
          <span>📍</span>
          {geoLoading ? 'Localisation en cours…' : 'Utiliser ma position actuelle'}
        </button>

        {msg && <p className={`text-xs ${msg.startsWith('✅') ? 'text-success' : 'text-danger'}`}>{msg}</p>}

        {!latitude && !longitude && (
          <p className="text-xs text-warning">⚠️ Aucune position enregistrée — vous ne recevrez pas de missions.</p>
        )}
      </div>
    </div>
  )
}

interface ProviderProfile {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  bio: string | null
  avatarUrl: string | null
  maxDistanceKm: number
  rating: number
  ratingCount: number
  priorityScore: number
  priorityTier: string
  latitude: number | null
  longitude: number | null
  paypalEmail: string | null
  iban: string | null
  bic: string | null
  specialties: string[]
}

interface ConnectStatus {
  configured: boolean
  enabled: boolean
}

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'En attente de paiement',
  AUTHORIZED: 'Paiement autorisé',
  CAPTURED: 'Paiement reçu',
  RELEASED: 'Versé',
  FAILED: 'Échec',
}

export default function ProfilPrestatairePage() {
  const [searchParams] = useSearchParams()
  const stripeParam = searchParams.get('stripe')

  const [profile, setProfile] = useState<ProviderProfile | null>(null)
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const [stripeLoading, setStripeLoading] = useState(false)
  const [stripeError, setStripeError] = useState('')
  const [paypalEmail, setPaypalEmail] = useState('')
  const [paypalSaving, setPaypalSaving] = useState(false)
  const [paypalMessage, setPaypalMessage] = useState('')
  const [iban, setIban] = useState('')
  const [bic, setBic] = useState('')
  const [ibanSaving, setIbanSaving] = useState(false)
  const [ibanMessage, setIbanMessage] = useState('')
  const [specialties, setSpecialties] = useState<string[]>([])
  const { byDomain: specialtyGroups, loading: specialtiesLoading } = useSpecialtyConfig()
  const [specialtiesSaving, setSpecialtiesSaving] = useState(false)
  const [specialtiesMessage, setSpecialtiesMessage] = useState('')
  const [maxDistance, setMaxDistance] = useState(50)
  const [maxDistanceSaving, setMaxDistanceSaving] = useState(false)
  const [maxDistanceMessage, setMaxDistanceMessage] = useState('')

  useEffect(() => {
    Promise.all([
      apiFetch<ProviderProfile>('/providers/me'),
      apiFetch<ConnectStatus>('/payments/stripe/provider/connect/status'),
    ])
      .then(([prof, status]) => {
        setProfile(prof)
        setConnectStatus(status)
        if (prof.paypalEmail) setPaypalEmail(prof.paypalEmail)
        if (prof.iban) setIban(prof.iban)
        if (prof.bic) setBic(prof.bic)
        if (prof.specialties?.length) setSpecialties(prof.specialties)
        if (prof.maxDistanceKm) setMaxDistance(prof.maxDistanceKm)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!profile?.avatarUrl) return
    const token = localStorage.getItem('velink_token')
    const [pId, fname] = profile.avatarUrl.split('/')
    fetch(`${API}/api/providers/avatar/${pId}/${fname}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => blob && setAvatarBlobUrl(URL.createObjectURL(blob)))
      .catch(() => {})
  }, [profile?.avatarUrl])

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarUploading(true)
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/providers/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
        body: fd,
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      setProfile((prev) => prev ? { ...prev, avatarUrl: data.avatarUrl } : prev)
      setAvatarBlobUrl(null)
    } catch { /* silencieux */ }
    finally { setAvatarUploading(false) }
  }

  async function handlePaypalSave() {
    if (!paypalEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail)) {
      setPaypalMessage('Adresse email invalide')
      return
    }
    setPaypalSaving(true)
    setPaypalMessage('')
    try {
      await apiFetch('/payments/paypal/provider/email', {
        method: 'PATCH',
        body: JSON.stringify({ email: paypalEmail }),
      })
      setProfile((prev) => prev ? { ...prev, paypalEmail } : prev)
      setPaypalMessage('✅ Email PayPal enregistré')
    } catch (e: unknown) {
      setPaypalMessage(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setPaypalSaving(false)
    }
  }

  async function handleSpecialtiesSave() {
    setSpecialtiesSaving(true)
    setSpecialtiesMessage('')
    try {
      await apiFetch('/providers/me/specialties', {
        method: 'PATCH',
        body: JSON.stringify({ specialties }),
      })
      setSpecialtiesMessage('✅ Spécialités enregistrées')
    } catch (e: unknown) {
      setSpecialtiesMessage(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setSpecialtiesSaving(false)
    }
  }

  async function handleIbanSave() {
    const clean = iban.replace(/\s/g, '').toUpperCase()
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,}$/.test(clean)) {
      setIbanMessage('IBAN invalide (ex: FR76 3000 6000 0112 3456 7890 189)')
      return
    }
    setIbanSaving(true)
    setIbanMessage('')
    try {
      await apiFetch('/providers/me/bank-account', {
        method: 'PATCH',
        body: JSON.stringify({ iban: clean, bic: bic || undefined }),
      })
      setProfile((prev) => prev ? { ...prev, iban: clean, bic: bic || null } : prev)
      setIbanMessage('✅ Coordonnées bancaires enregistrées')
    } catch (e: unknown) {
      setIbanMessage(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setIbanSaving(false)
    }
  }

  async function handleMaxDistanceSave() {
    setMaxDistanceSaving(true)
    setMaxDistanceMessage('')
    try {
      await apiFetch('/providers/me/max-distance', {
        method: 'PATCH',
        body: JSON.stringify({ maxDistanceKm: maxDistance }),
      })
      setProfile((prev) => prev ? { ...prev, maxDistanceKm: maxDistance } : prev)
      setMaxDistanceMessage('✅ Distance maximale enregistrée')
    } catch (e: unknown) {
      setMaxDistanceMessage(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setMaxDistanceSaving(false)
    }
  }

  async function handleStripeConnect() {
    setStripeLoading(true)
    setStripeError('')
    try {
      const { url } = await apiFetch<{ url: string }>('/payments/stripe/provider/connect', {
        method: 'POST',
      })
      window.location.href = url
    } catch (e: unknown) {
      setStripeError(e instanceof Error ? e.message : 'Erreur Stripe')
      setStripeLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="glass-card h-32 animate-pulse" />
        <div className="glass-card h-48 animate-pulse" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Mon profil</h1>
        <p className="text-text-secondary text-sm mt-1">Informations et configuration des paiements</p>
      </div>

      {/* Stripe return banners */}
      {stripeParam === 'success' && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-success/10 border border-success/25 text-success text-sm flex items-center gap-2">
          ✅ Votre compte Stripe est configuré ! Vous pouvez maintenant recevoir des paiements.
        </div>
      )}
      {stripeParam === 'refresh' && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-warning/10 border border-warning/25 text-warning text-sm">
          ⚠️ La configuration a expiré. Veuillez recommencer.
        </div>
      )}

      <div className="space-y-4">
        {/* Infos */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Informations</h2>
          <div className="flex items-center gap-4 mb-5">
            <div className="relative flex-shrink-0 group">
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarUploading}
                className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-accent/30 hover:border-accent/60 transition-all focus:outline-none"
                title="Changer la photo"
              >
                {avatarBlobUrl ? (
                  <img src={avatarBlobUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-accent/20 flex items-center justify-center">
                    <span className="text-accent font-bold text-2xl">
                      {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                    </span>
                  </div>
                )}
              </button>
              <div
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              >
                {avatarUploading ? (
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                )}
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleAvatarUpload}
              />
            </div>
            <div>
              <p className="text-text-primary font-bold text-xl">
                {profile?.firstName} {profile?.lastName}
              </p>
              <p className="text-text-secondary text-sm">{profile?.email}</p>
              {(profile?.ratingCount ?? 0) > 0 && (
                <p className="text-warning text-sm mt-0.5">
                  ★ {profile?.rating.toFixed(1)} ({profile?.ratingCount} avis)
                </p>
              )}
              {profile?.priorityTier === 'PREMIUM' && (
                <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/35 text-[#F59E0B] font-semibold">
                  ⭐ Top prestataire
                </span>
              )}
              {profile?.priorityTier === 'ACTIVE' && (
                <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-2 py-0.5 rounded-full bg-accent/10 border border-accent/25 text-accent font-semibold">
                  ✦ Prestataire actif
                </span>
              )}
              <p className="text-text-muted text-xs mt-1">
                Cliquez sur la photo pour la modifier
              </p>
            </div>
          </div>

          {/* Score de priorité */}
          {(profile?.priorityScore ?? 0) > 0 && (
            <div className="glass-card p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-text-secondary text-xs font-medium">Score Velink</p>
                <span className="text-text-primary text-sm font-bold">{Math.round(profile!.priorityScore)}<span className="text-text-muted font-normal">/100</span></span>
              </div>
              <div className="w-full h-2 rounded-full bg-white/[0.08] overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${profile!.priorityTier === 'PREMIUM' ? 'bg-[#F59E0B]' : profile!.priorityTier === 'ACTIVE' ? 'bg-accent' : 'bg-white/30'}`}
                  style={{ width: `${Math.round(profile!.priorityScore)}%` }}
                />
              </div>
              <p className="text-text-muted text-[10px] mt-1.5">
                {profile!.priorityTier === 'PREMIUM'
                  ? '⭐ Vous avez accès aux nouvelles missions en avant-première (fenêtre exclusive de 15 min)'
                  : profile!.priorityTier === 'ACTIVE'
                  ? '✦ Continuez à accepter et réaliser des missions pour atteindre le statut Top prestataire'
                  : 'Complétez votre profil et réalisez vos premières missions pour améliorer votre score'}
              </p>
            </div>
          )}

          {profile?.bio && (
            <div>
              <p className="text-text-muted text-xs mb-1">Présentation</p>
              <p className="text-text-secondary text-sm leading-relaxed">{profile.bio}</p>
            </div>
          )}

        </div>

        {/* Position */}
        <LocationSection
          latitude={profile?.latitude ?? null}
          longitude={profile?.longitude ?? null}
          onUpdated={(lat, lng) => setProfile((prev) => prev ? { ...prev, latitude: lat, longitude: lng } : prev)}
        />

        {/* Distance maximale */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Distance maximale</h2>
          <p className="text-text-secondary text-sm mb-5 leading-relaxed">
            Définissez la distance maximale que vous êtes prêt à parcourir pour une mission.
            Seules les missions dans ce rayon vous seront proposées.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary text-sm">10 km</span>
              <span className="text-text-primary font-bold text-lg">{maxDistance} km</span>
              <span className="text-text-secondary text-sm">500 km</span>
            </div>
            <input
              type="range"
              min={10}
              max={500}
              step={10}
              value={maxDistance}
              onChange={(e) => setMaxDistance(Number(e.target.value))}
              className="w-full accent-[#6E7FFF] cursor-pointer"
            />
            <div className="flex gap-2 flex-wrap">
              {[20, 50, 100, 200].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setMaxDistance(d)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    maxDistance === d
                      ? 'bg-accent/20 border-accent/40 text-accent'
                      : 'border-white/[0.12] text-text-muted hover:border-white/[0.25]'
                  }`}
                >
                  {d} km
                </button>
              ))}
            </div>
            {maxDistanceMessage && (
              <p className={`text-xs ${maxDistanceMessage.startsWith('✅') ? 'text-success' : 'text-danger'}`}>
                {maxDistanceMessage}
              </p>
            )}
            <button
              onClick={handleMaxDistanceSave}
              disabled={maxDistanceSaving}
              className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl w-full"
            >
              {maxDistanceSaving ? 'Enregistrement…' : '💾 Enregistrer la distance'}
            </button>
          </div>
        </div>

        {/* Spécialités */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
            Mes spécialités
          </h2>
          <p className="text-text-secondary text-sm mb-5 leading-relaxed">
            Cochez vos qualifications. Vous recevrez uniquement les missions correspondant à vos spécialités.
          </p>

          <div className="space-y-5 mb-5">
            {specialtiesLoading && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-14 rounded-xl bg-white/[0.04] animate-pulse" />)}
              </div>
            )}
            {specialtyGroups.map((group) => (
              <div key={group.id ?? 'none'}>
                <p className="text-[11px] font-semibold text-text-muted uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <span>{group.icon}</span> {group.label}
                </p>
                <div className="space-y-2">
                  {group.specialties.map((s, i) => {
                    const checked = specialties.includes(s.specialty)
                    // `subcategory` n'est pas déclaré sur le type SpecialtyConfig partagé mais peut être renvoyé par l'API
                    const sub = (s as { subcategory?: string }).subcategory ?? ''
                    const showSub = sub && (i === 0 || ((group.specialties[i - 1] as { subcategory?: string }).subcategory ?? '') !== sub)
                    return (
                      <div key={s.specialty} className={showSub && i > 0 ? 'pt-2' : ''}>
                      {showSub && <p className="text-[10px] text-text-muted mb-1.5 ml-1">{sub}</p>}
                      <label
                        className={[
                          'flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all',
                          checked
                            ? 'bg-accent/10 border-accent/30'
                            : 'border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.03]',
                        ].join(' ')}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-[#6E7FFF]"
                          checked={checked}
                          onChange={(e) =>
                            setSpecialties((prev) =>
                              e.target.checked ? [...prev, s.specialty] : prev.filter((x) => x !== s.specialty),
                            )
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span>{s.icon}</span>
                            <span className={`text-sm font-medium ${checked ? 'text-accent' : 'text-text-primary'}`}>
                              {s.label}
                            </span>
                          </div>
                          {s.description && (
                            <p className="text-text-muted text-xs mt-0.5 leading-relaxed">{s.description}</p>
                          )}
                        </div>
                      </label>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          {specialtiesMessage && (
            <p className={`text-xs mb-3 ${specialtiesMessage.startsWith('✅') ? 'text-success' : 'text-danger'}`}>
              {specialtiesMessage}
            </p>
          )}

          <button
            onClick={handleSpecialtiesSave}
            disabled={specialtiesSaving}
            className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl w-full"
          >
            {specialtiesSaving ? 'Enregistrement…' : '💾 Enregistrer mes spécialités'}
          </button>
        </div>

        {/* Paiements Stripe Connect */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">
            Paiements
          </h2>

          {connectStatus?.enabled ? (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-success/10 border border-success/25">
              <span className="text-2xl">✅</span>
              <div>
                <p className="text-success font-medium text-sm">Compte Stripe activé</p>
                <p className="text-text-secondary text-xs mt-0.5">
                  Vous pouvez recevoir des paiements pour vos missions.
                </p>
              </div>
            </div>
          ) : connectStatus?.configured ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-warning/10 border border-warning/25">
                <span className="text-2xl">⏳</span>
                <div>
                  <p className="text-warning font-medium text-sm">Vérification en cours</p>
                  <p className="text-text-secondary text-xs mt-0.5">
                    Stripe examine votre dossier. Cela peut prendre quelques minutes.
                  </p>
                </div>
              </div>
              <button
                onClick={handleStripeConnect}
                disabled={stripeLoading}
                className="glass-btn px-5 py-2.5 text-sm rounded-xl w-full"
              >
                {stripeLoading ? 'Redirection…' : '↗ Compléter la configuration'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                <p className="text-text-primary font-medium text-sm mb-2">Configurez votre compte Stripe</p>
                <p className="text-text-secondary text-xs leading-relaxed">
                  Pour recevoir vos paiements via Velink, vous devez connecter un compte Stripe.
                  L'inscription est gratuite et prend environ 5 minutes.
                </p>
                <ul className="mt-3 space-y-1">
                  {['Paiements sécurisés', 'Virement automatique sous 2 jours', 'Tableau de bord Stripe'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-text-secondary">
                      <span className="text-success">✓</span> {item}
                    </li>
                  ))}
                </ul>
              </div>

              {stripeError && (
                <div className="px-3 py-2 rounded-xl bg-danger/10 border border-danger/25 text-danger text-xs">
                  {stripeError}
                </div>
              )}

              <button
                onClick={handleStripeConnect}
                disabled={stripeLoading}
                className="glass-btn-accent w-full py-3 text-sm rounded-xl"
              >
                {stripeLoading ? 'Redirection vers Stripe…' : '↗ Configurer mes paiements Stripe'}
              </button>
            </div>
          )}
        </div>

        {/* PayPal */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">
            PayPal
          </h2>

          <p className="text-text-secondary text-sm mb-4 leading-relaxed">
            Renseignez votre email PayPal pour recevoir vos paiements directement via PayPal lorsqu'une entreprise choisit ce mode.
          </p>

          <div className="space-y-3">
            <div>
              <label htmlFor="paypalEmail" className="glass-label">Adresse email PayPal</label>
              <input
                type="email"
                id="paypalEmail"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="votre-email@paypal.com"
                className="glass-input"
              />
            </div>

            {paypalMessage && (
              <p className={`text-xs ${paypalMessage.startsWith('✅') ? 'text-success' : 'text-danger'}`}>
                {paypalMessage}
              </p>
            )}

            <button
              onClick={handlePaypalSave}
              disabled={paypalSaving}
              className="glass-btn px-5 py-2.5 text-sm rounded-xl w-full"
            >
              {paypalSaving ? 'Enregistrement…' : profile?.paypalEmail ? '✏️ Mettre à jour' : '💾 Enregistrer'}
            </button>

            {profile?.paypalEmail && (
              <p className="text-text-muted text-xs">
                Email actuel : <span className="text-text-secondary">{profile.paypalEmail}</span>
              </p>
            )}
          </div>
        </div>

        {/* Virement bancaire / RIB */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">
            Virement bancaire (RIB / IBAN)
          </h2>

          <p className="text-text-secondary text-sm mb-4 leading-relaxed">
            Renseignez votre IBAN pour recevoir vos paiements par virement bancaire lorsqu'une entreprise choisit ce mode.
          </p>

          <div className="space-y-3">
            <div>
              <label htmlFor="iban" className="glass-label">IBAN</label>
              <input
                type="text"
                id="iban"
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase())}
                placeholder="FR76 3000 6000 0112 3456 7890 189"
                className="glass-input font-mono tracking-wider"
                maxLength={34}
              />
            </div>
            <div>
              <label htmlFor="bic" className="glass-label">BIC / SWIFT <span className="text-text-muted font-normal">(optionnel)</span></label>
              <input
                type="text"
                id="bic"
                value={bic}
                onChange={(e) => setBic(e.target.value.toUpperCase())}
                placeholder="BNPAFRPPXXX"
                className="glass-input font-mono tracking-wider"
                maxLength={11}
              />
            </div>

            {ibanMessage && (
              <p className={`text-xs ${ibanMessage.startsWith('✅') ? 'text-success' : 'text-danger'}`}>
                {ibanMessage}
              </p>
            )}

            <button
              onClick={handleIbanSave}
              disabled={ibanSaving}
              className="glass-btn px-5 py-2.5 text-sm rounded-xl w-full"
            >
              {ibanSaving ? 'Enregistrement…' : profile?.iban ? '✏️ Mettre à jour' : '💾 Enregistrer'}
            </button>

            {profile?.iban && (
              <p className="text-text-muted text-xs">
                IBAN actuel : <span className="text-text-secondary font-mono">{profile.iban.replace(/(.{4})/g, '$1 ').trim()}</span>
              </p>
            )}
          </div>
        </div>

        {/* Modifier le profil */}
        <ProfileEditSection />

        {/* Changer l'email */}
        <EmailSection
          currentEmail={profile?.email ?? null}
          onEmailChanged={(email, token) => {
            localStorage.setItem('velink_token', token)
            setProfile((prev) => prev ? { ...prev, email } : prev)
          }}
        />

        {/* Changer le mot de passe */}
        <PasswordSection />
      </div>
    </div>
  )
}
